























































































































































import { Component, Inject, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums/Data
import { BrandConfigKeys } from "@/data/constants";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IClientAuthProviderData,
  IClientAuthProviderMethods
} from "@/models/providers/clientAuth";
import { GuestRoutes } from "@/data/enums/router";
import { mapGetters } from "vuex";

@Component({
  components: {
    "client-auth-provider": () =>
      import("@/components/app/global/auth/clientAuth/clientAuthProvider.vue"),
    "client-auth-register-fields": () =>
      import(
        "@/components/app/global/auth/clientAuth/clientAuthRegisterFields.vue"
      ),
    "client-auth-login-fields": () =>
      import(
        "@/components/app/global/auth/clientAuth/clientAuthLoginFields.vue"
      )
  },
  computed: {
    ...mapGetters({
      hasRegistrationEnabled: "brand/hasRegistrationEnabled"
    })
  }
})
export default class BasketAuthenticate extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    clientAuthRegisterFields: HTMLFormElement;
  };

  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Vuex

  public readonly hasRegistrationEnabled!: boolean;

  // Variables

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public isProcessingGuest = false;
  public hasRequestError = false;

  public register = true;
  public brandConfigKey = BrandConfigKeys.GUEST_CHECKOUT_ENABLED;
  public forgottenPasswordRouteName = GuestRoutes.FORGOTTEN_PASSWORD;

  // Life cycle methods

  public async created() {
    this.register = this.hasRegistrationEnabled;
    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      await this.getBrandConfig();
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getBrandConfig() {
    return this.$store.dispatch("brand/getConfig", {
      keys: [this.brandConfigKey],
      brandId: this.basket.brand_id
    });
  }

  public async validateBeforeRegister({
    $clientAuthData,
    $clientAuthMethods
  }: {
    $clientAuthData: IClientAuthProviderData;
    $clientAuthMethods: IClientAuthProviderMethods;
  }) {
    try {
      this.isProcessing = true;
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      // Upload any custom field images
      await this.$refs.clientAuthRegisterFields.uploadCustomImages(true);
      // Generate fresh reCaptcha token
      const token = await this.$store.dispatch(
        "utils/recaptcha/generateToken",
        "client_register"
      );
      // Add token to registration payload
      $clientAuthData.form.recaptcha_token = token;
      // Do registration call
      await $clientAuthMethods.registerClient();
      // Track login event
      await $clientAuthMethods.login({ trackEvent: false });
      // Setup app for client
      await this.$basketMethods.setAppForClient();
    } catch (error) {
      if (error === "cancelled") return;
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async validateBeforeLogin({
    $clientAuthMethods
  }: {
    $clientAuthMethods: IClientAuthProviderMethods;
  }) {
    try {
      this.isProcessing = true;
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      // Do login call
      await $clientAuthMethods.login({ trackEvent: false });
      // Setup app for client
      await this.$basketMethods.setAppForClient();
    } catch (error) {
      if (error === "cancelled") return;
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async checkoutAsGuest(clientAuthMethods: IClientAuthProviderMethods) {
    if (this.isProcessingGuest) return;
    try {
      this.isProcessingGuest = true;
      await clientAuthMethods.registerGuest();
      await this.$basketMethods.setAppForClient();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessingGuest = false;
    }
  }

  // Computed

  public get basket() {
    return this.$basketData().basket;
  }

  public get hasGuestClientCheckout() {
    return (
      this.$store.getters["brand/config"][this.brandConfigKey] &&
      !this.$_.some(this.basket.products, p => p.billing_cycle_months > 0)
    );
  }
}
